var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { Accordion, AccordionSet, Button, Loading, Modal, ModalFooter, TextArea, } from "@folio/stripes/components";
import { useTitleLibraryNoteDialog, useUpsertMyLibraryNote, } from "../../hooks/useTitleLibraryNoteDialog";
import { hasHttpResponse } from "../../utils/apiUtils";
import { LIBRARY_NOTE_MAX_LENGTH, LIBRARY_NOTE_WARNING_THRESHOLD, } from "../../types/titleLibraryNote";
import styles from "./LibraryNoteDialog.module.css";
var MAX = LIBRARY_NOTE_MAX_LENGTH;
var WARNING_THRESHOLD = LIBRARY_NOTE_WARNING_THRESHOLD;
export var LibraryNoteDialog = function (_a) {
    var _b, _c, _d, _e, _f;
    var open = _a.open, titleId = _a.titleId, titleName = _a.titleName, onClose = _a.onClose;
    var formatMessage = useIntl().formatMessage;
    var _g = useTitleLibraryNoteDialog(titleId, open), data = _g.data, isLoading = _g.isLoading, isError = _g.isError;
    var mutation = useUpsertMyLibraryNote(titleId);
    var _h = useState(""), text = _h[0], setText = _h[1];
    var _j = useState({}), openAccordions = _j[0], setOpenAccordions = _j[1];
    useEffect(function () {
        var _a, _b;
        if (open) {
            setText((_b = (_a = data === null || data === void 0 ? void 0 : data.myNote) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : "");
        }
        else {
            setText("");
            setOpenAccordions({});
            mutation.reset();
        }
        // mutation reference changes every render; including it would loop on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, (_b = data === null || data === void 0 ? void 0 : data.myNote) === null || _b === void 0 ? void 0 : _b.text]);
    var tooLong = text.length > MAX;
    var counterClass = useMemo(function () {
        if (tooLong)
            return styles.counterError;
        if (text.length > WARNING_THRESHOLD)
            return styles.counterWarning;
        return styles.counter;
    }, [tooLong, text.length]);
    var handleSave = useCallback(function () {
        mutation.mutate(text, { onSuccess: function () { return onClose(); } });
    }, [mutation, text, onClose]);
    var handleToggle = useCallback(function (_a) {
        var id = _a.id;
        setOpenAccordions(function (s) {
            var _a;
            return (__assign(__assign({}, s), (_a = {}, _a[id] = !s[id], _a)));
        });
    }, []);
    var errorMsg = useMemo(function () {
        if (!mutation.error)
            return null;
        var status = hasHttpResponse(mutation.error)
            ? mutation.error.response.status
            : null;
        // the only bean validation on this endpoint is the 2000 character limit, and the
        // module answers 422 for both bean and domain validation since issue #74026
        if (status === 422)
            return formatMessage({
                id: "ui-br-legaldeposits.title.libraryNote.dialog.errors.tooLong",
            });
        if (status === 403)
            return formatMessage({
                id: "ui-br-legaldeposits.title.libraryNote.dialog.errors.noLibrary",
            });
        return formatMessage({
            id: "ui-br-legaldeposits.title.libraryNote.dialog.errors.generic",
        });
    }, [mutation.error, formatMessage]);
    var userHasLibrary = (_c = data === null || data === void 0 ? void 0 : data.userHasLibrary) !== null && _c !== void 0 ? _c : false;
    var others = (_d = data === null || data === void 0 ? void 0 : data.otherNotes) !== null && _d !== void 0 ? _d : [];
    var footer = useMemo(function () {
        if (!userHasLibrary) {
            return (React.createElement(ModalFooter, null,
                React.createElement(Button, { "data-testid": "library-note-close", onClick: onClose },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.close" }))));
        }
        return (React.createElement(ModalFooter, null,
            React.createElement(Button, { "data-testid": "library-note-save", buttonStyle: "primary", onClick: handleSave, disabled: tooLong || mutation.isLoading },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.save" })),
            React.createElement(Button, { "data-testid": "library-note-close", onClick: onClose },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.cancel" }))));
    }, [userHasLibrary, handleSave, onClose, tooLong, mutation.isLoading]);
    return (React.createElement(Modal, { open: open, onClose: onClose, label: formatMessage({ id: "ui-br-legaldeposits.title.libraryNote.dialog.title" }, { title: titleName }), size: "medium", dismissible: true, footer: footer },
        isLoading && React.createElement(Loading, null),
        isError && (React.createElement("div", { className: styles.error },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.common.error" }))),
        !isLoading && !isError && data && (React.createElement(React.Fragment, null,
            userHasLibrary && (React.createElement("div", { className: styles.section },
                React.createElement("div", { className: styles.sectionHeader },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.section.my" })),
                data.myNote && (React.createElement("div", { className: styles.lastModified },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.lastModified", values: {
                            date: (React.createElement(FormattedDate, { value: data.myNote.lastModifiedAt })),
                            user: (_f = (_e = data.myNote.lastModifiedBy) === null || _e === void 0 ? void 0 : _e.fullName) !== null && _f !== void 0 ? _f : formatMessage({
                                id: "ui-br-legaldeposits.title.libraryNote.dialog.unknownUser",
                            }),
                        } }))),
                React.createElement(TextArea, { "data-testid": "library-note-textarea", value: text, onChange: function (e) {
                        return setText(e.target.value);
                    }, placeholder: formatMessage({
                        id: "ui-br-legaldeposits.title.libraryNote.dialog.placeholder",
                    }) }),
                React.createElement("div", { className: counterClass, "data-testid": "library-note-counter" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.counter", values: { count: text.length } })),
                errorMsg && React.createElement("div", { className: styles.error }, errorMsg))),
            React.createElement("div", { className: styles.section },
                React.createElement("div", { className: styles.sectionHeader },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.section.others", values: { count: others.length } })),
                others.length === 0 ? (React.createElement("div", { className: styles.othersEmpty, "data-testid": "library-note-others-empty" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.others.empty" }))) : (React.createElement(AccordionSet, null, others.map(function (it) {
                    var _a, _b, _c;
                    return (React.createElement(Accordion, { key: it.id, id: it.id, "data-testid": "library-note-other-".concat(it.libraryId), open: (_a = openAccordions[it.id]) !== null && _a !== void 0 ? _a : false, onToggle: handleToggle, label: it.libraryName },
                        React.createElement("div", { className: styles.expandedHeader },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.title.libraryNote.dialog.others.expandedHeader", values: {
                                    date: React.createElement(FormattedDate, { value: it.lastModifiedAt }),
                                    user: (_c = (_b = it.lastModifiedBy) === null || _b === void 0 ? void 0 : _b.fullName) !== null && _c !== void 0 ? _c : formatMessage({
                                        id: "ui-br-legaldeposits.title.libraryNote.dialog.unknownUser",
                                    }),
                                } })),
                        React.createElement("div", { className: styles.noteBody }, it.text)));
                }))))))));
};
